import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import AdminNavbar from '../components/AdminNavbar';
import { BarChart3, RefreshCw, TrendingUp, Users, DollarSign, ArrowUpRight, ArrowDownRight } from 'lucide-react';

const AdminReportsPage = () => {
    const [data, setData] = useState({ dailyStats: [], totals: {} });
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState('7d');

    useEffect(() => { fetchReports(); }, [period]);

    const fetchReports = async () => {
        try {
            setLoading(true);
            const res = await api.get(`/admin/reports?period=${period}`);
            setData(res.data);
        } catch (error) {
            toast.error('Failed to load reports');
        } finally {
            setLoading(false);
        }
    };

    const StatCard = ({ title, value, icon: Icon, color, subtext }) => (
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-gray-400 text-sm">{title}</p>
                    <p className="text-2xl font-bold text-white mt-1">{value}</p>
                    {subtext && <p className="text-gray-500 text-xs mt-1">{subtext}</p>}
                </div>
                <div className={`p-3 rounded-lg ${color}`}><Icon size={24} /></div>
            </div>
        </div>
    );

    const maxVolume = Math.max(...data.dailyStats.map(d => d.volume), 1);

    return (
        <div className="min-h-screen bg-slate-900">
            <AdminNavbar />
            <div className="bg-slate-800 border-b border-slate-700 px-6 py-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-white flex items-center gap-2"><BarChart3 className="text-cyan-400" /> Reports & Analytics</h1>
                        <p className="text-gray-400 text-sm">Platform performance overview</p>
                    </div>
                    <div className="flex gap-2">
                        <select value={period} onChange={e => setPeriod(e.target.value)} className="px-4 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white">
                            <option value="7d">Last 7 Days</option>
                            <option value="30d">Last 30 Days</option>
                            <option value="90d">Last 90 Days</option>
                        </select>
                        <button onClick={fetchReports} className="px-4 py-2 bg-slate-700 text-white rounded-lg hover:bg-slate-600"><RefreshCw size={18} /></button>
                    </div>
                </div>
            </div>

            <div className="p-6">
                {loading ? (
                    <div className="text-center py-12 text-gray-400">Loading reports...</div>
                ) : (
                    <>
                        {/* Stats Cards */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                            <StatCard title="Total Users" value={data.totals.users?.toLocaleString() || 0} icon={Users} color="bg-blue-500/20 text-blue-400" />
                            <StatCard title="Total Orders" value={data.totals.orders?.toLocaleString() || 0} icon={TrendingUp} color="bg-green-500/20 text-green-400" />
                            <StatCard title="Total Volume" value={`$${(data.totals.volume || 0).toLocaleString()}`} icon={BarChart3} color="bg-purple-500/20 text-purple-400" />
                            <StatCard title="Total Deposits" value={`$${(data.totals.deposits || 0).toLocaleString()}`} icon={ArrowUpRight} color="bg-cyan-500/20 text-cyan-400" />
                            <StatCard title="Total Withdrawals" value={`$${(data.totals.withdrawals || 0).toLocaleString()}`} icon={ArrowDownRight} color="bg-orange-500/20 text-orange-400" />
                        </div>

                        {/* Volume Chart */}
                        <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
                            <h2 className="text-lg font-bold text-white mb-4">Daily Volume</h2>
                            <div className="flex items-end gap-1 h-48">
                                {data.dailyStats.map((d, i) => (
                                    <div key={i} className="flex-1 flex flex-col items-center">
                                        <div className="w-full bg-cyan-500/30 rounded-t" style={{ height: `${(d.volume / maxVolume) * 100}%`, minHeight: '4px' }}>
                                            <div className="w-full h-full bg-cyan-500 rounded-t hover:bg-cyan-400 transition cursor-pointer" title={`$${d.volume.toLocaleString()}`} />
                                        </div>
                                        <span className="text-xs text-gray-500 mt-2 rotate-45 origin-left">{d.date.slice(5)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Daily Stats Table */}
                        <div className="bg-slate-800 rounded-xl border border-slate-700 mt-6 overflow-hidden">
                            <h2 className="text-lg font-bold text-white p-4 border-b border-slate-700">Daily Statistics</h2>
                            <table className="w-full">
                                <thead className="bg-slate-900/50">
                                    <tr className="text-left text-gray-400 text-sm">
                                        <th className="px-4 py-3">Date</th>
                                        <th className="px-4 py-3">New Users</th>
                                        <th className="px-4 py-3">Orders</th>
                                        <th className="px-4 py-3">Volume</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-700">
                                    {data.dailyStats.slice(-10).reverse().map((d, i) => (
                                        <tr key={i} className="hover:bg-slate-700/30">
                                            <td className="px-4 py-3 text-white">{d.date}</td>
                                            <td className="px-4 py-3 text-gray-300">{d.newUsers}</td>
                                            <td className="px-4 py-3 text-gray-300">{d.orders}</td>
                                            <td className="px-4 py-3 text-green-400">${d.volume.toLocaleString()}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AdminReportsPage;
